import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tv, ChevronRight } from 'lucide-react';
import { Container, PageHeader, Section, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState, type StatusTone } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useFastChannelServices, type FastChannelService } from '@/api/fastchannel';
import { formatDate } from '@/lib/format-date';
import { brand } from '@/config/brand';
import styles from './Fastchannel.module.css';

const BASE = '/services/fastchannel';

function statusTone(status: string): StatusTone {
  const s = status.toLowerCase();
  if (s === 'active' || s === 'running' || s === 'enabled') return 'success';
  if (s === 'pending' || s === 'provisioning') return 'warning';
  return 'muted';
}

export function ServicePicker() {
  const navigate = useNavigate();
  const { data: services = [], isLoading, isError, refetch } = useFastChannelServices();

  // With a single FastChannel service there's no choice to make — jump straight in.
  useEffect(() => {
    if (!isLoading && !isError && services.length === 1) {
      navigate(`${BASE}/${services[0].id}`, { replace: true });
    }
  }, [isLoading, isError, services, navigate]);

  return (
    <Container size="xl">
      <PageHeader
        icon={<Tv size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href="/services" onClick={(e) => { e.preventDefault(); navigate('/services'); }}>Services</BreadcrumbItem>
            <BreadcrumbItem current>FastChannel</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="FastChannel"
        description="FAST playout — manage channels, schedules and playout for your tenants."
      />

      <Section title="Your channels services" description="Pick a service to manage its channels.">
        {isError ? (
          <Alert
            tone="danger"
            title="Couldn't load services"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            The service catalog is temporarily unavailable. Please try again.
          </Alert>
        ) : isLoading ? (
          <div className={styles.pickerGrid}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 132 }} />
            ))}
          </div>
        ) : services.length === 0 ? (
          <EmptyState
            icon={<Tv size={20} />}
            title="No FastChannel services"
            description={`You don't have any FAST playout services provisioned yet. Contact ${brand.shortName} to get started.`}
          />
        ) : (
          <div className={styles.pickerGrid}>
            {services.map((svc: FastChannelService) => (
              <button
                key={svc.id}
                type="button"
                className={styles.pickerCard}
                onClick={() => navigate(`${BASE}/${svc.id}`)}
              >
                <div className={styles.pickerHead}>
                  <span className={styles.pickerIcon}>
                    <Tv size={20} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className={styles.pickerName}>{svc.service_name || 'FastChannel service'}</div>
                    <div className={styles.pickerSub}>{svc.provider_name ?? 'FAST Channel'}</div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--vn-space-2)',
                  }}
                >
                  <StatusPill tone={statusTone(svc.status)}>{svc.status || 'Unknown'}</StatusPill>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--vn-space-1)',
                      color: 'var(--vn-fg-muted)',
                      fontSize: 'var(--vn-font-size-sm)',
                    }}
                  >
                    {svc.created_at ? `Since ${formatDate(svc.created_at)}` : 'Open'}
                    <ChevronRight size={14} />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </Section>
    </Container>
  );
}
